import React from 'react';
import { makeStyles } from 'src/makeStyles';

import SortIcon from '@mui/icons-material/Sort';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import FBDropdownButton from './Component';
import FBToggleList from './Component/mobile';
import { colors, fontSize } from '../../constants/styleConstants';
import { TP, PRODUCTION_VIEW_TYPES } from '../../constants/consts';
import { useTranslation } from '../../src/i18n';
import { getOptions } from '../InteractiveFilterPanel/aggregationUtil';

const useStyles = makeStyles()(() => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },

  label: {
    fontSize: fontSize.smallTitle,
    color: colors.darkText,
    marginRight: '8px',
    opacity: '0.6',
  },
  fullList: {
    display: 'flex',
    minHeight: '320px',
    maxHeight: '320px',
  },
  list1: {
    width: '40%',
  },
  list2: {
    width: '60%',
    overflow: 'scroll',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '65px',
    padding: '0px 18px',
  },
  headerLabel: {
    fontSize: fontSize.large,
    fontWeight: 'bold ',
    opacity: '0.6',
  },
  content: {
    borderRadius: '8px',
    border: `solid 1px ${colors.border.grey1}`,
    backgroundColor: colors.lightGrey,
    color: colors.textColor,
    '&:hover': {
      backgroundColor: colors.blue1,
    },
  },
  selected: {
    backgroundColor: `${colors.blue1} !important`,
    borderRight: `solid 5px ${colors.blue3}`,
    '&:hover': {
      backgroundColor: colors.blue1,
    },
    color: colors.blue3,
    fontWeight: 'bold',
  },
  mobileSearch_icon: {
    width: 32,
    height: 32,
    border: `solid 1px ${colors.border.grey1}`,
    backgroundColor: colors.white,
  },
  closeIcon: {
    background: 'transparent',
    height: 18,
    width: 18,
  },
  button: {
    borderRadius: '8px',
    border: `solid 1px ${colors.border.grey1}`,
    backgroundColor: colors.white,
    '&:hover': {
      border: `solid 1px ${colors.blue3}`,
      backgroundColor: colors.blue1,
    },
  },
  customStyle: {
    textTransform: 'none',
    fontSize: fontSize.smallTitle,
    margin: '2px 4px 2px 0px',
    marginRight: '8px',
  },
  icon: {
    color: colors.blue3,
    fontSize: fontSize.large,
  },
  toggleButton: {
    marginLeft: 'auto',
  },
  sortLabel: {
    paddingRight: '10px',
    fontWeight: 'bold',
    color: colors.gray,
  },
}));

function filterReducer(state, { type, payload }) {
  switch (type) {
    case 'SELECT_LANGUAGE':
    case 'SELECT_SUBTITLE':
    case 'SELECT_COUNTRY':
    case 'SELECT_SEASON':
    case 'SELECT_SORT':
      // eslint-disable-next-line no-case-declarations
      const { aggregation_query, aggregation_type, ...existingFilters } = state;
      return {
        ...state,
        ...existingFilters,
      };
    case 'UPDATE_DATE': {
      const new_date_from = payload.new_date_from ? payload.new_date_from : state.date_from;
      const new_date_to = payload.new_date_to ? payload.new_date_to : state.date_to;
      const localState = { ...state };
      delete localState.season_id;
      return { ...localState, date_to: new_date_to, date_from: new_date_from };
    }
    case 'UPDATE_SEASON': {
      const localState = { ...state };
      delete localState.date_from;
      delete localState.date_to;
      return { ...localState, season_id: payload.season_id };
    }
    case 'UPDATE_QUERY': {
      return {
        ...state,
        ...{
          ...payload.state,
        },
      };
    }
    case 'RESET_QUERY': {
      return {
        ...payload.state,
      };
    }
    case 'RESET_FILTERS': {
      const updatedQuery = { ...state };
      delete updatedQuery[payload];
      return {
        ...updatedQuery,
      };
    }

    default: {
      throw new Error(`Unsupported type: ${type}`);
    }
  }
}

const fetchSortOptions = (page, query, viewType, t) => {
  const SORT_FIELDS = [
    { name: `${t(`${TP}.m_MUSICALWORK`)} (${t(`${TP}.SORT_DIRECTION_ASC`)})`, id: 'work.name', alternate: 'work_id' },
    { name: `${t(`${TP}.m_MUSICALWORK`)} (${t(`${TP}.SORT_DIRECTION_DESC`)})`, id: '-work.name', alternate: 'work_id' },
    {
      name: `${t(`${TP}.m_COMPOSER`)} (${t(`${TP}.SORT_DIRECTION_ASC`)})`,
      id: 'workComposer.name',
      alternate: 'composer_id',
    },
    {
      name: `${t(`${TP}.m_COMPOSER`)} (${t(`${TP}.SORT_DIRECTION_DESC`)})`,
      id: '-workComposer.name',
      alternate: 'composer_id',
    },
    {
      name: `${t(`${TP}.BILLING_BIL_INFO_COMPANY`)} (${t(`${TP}.SORT_DIRECTION_ASC`)})`,
      id: 'company.name',
      alternate: 'company_id',
    },
    {
      name: `${t(`${TP}.BILLING_BIL_INFO_COMPANY`)} (${t(`${TP}.SORT_DIRECTION_DESC`)})`,
      id: '-company.name',
      alternate: 'company_id',
    },
  ];

  const SORT_FIELDS_VIDEO = [
    {
      name: `${t(`${TP}.FN_LATEST_PERFORMANCE`)}`,
      id: '-performance',
      alternate: 'company_id',
    },
    {
      name: `${t(`${TP}.FN_OLDEST_PERFORMANCE`)}`,
      id: 'performance',
      alternate: 'company_id',
    },
    {
      name: `${t(`${TP}.FN_AVAILABILITY`)}`,
      id: 'expiringAt',
      alternate: 'expiringAt',
    },
  ];

  const SORT_FIELDS_OPTIONAL = [
    { name: `${t(`${TP}.m_CITY`)} (${t(`${TP}.SORT_DIRECTION_ASC`)})`, id: 'city.name', alternate: 'city_id' },
    { name: `${t(`${TP}.m_CITY`)} (${t(`${TP}.SORT_DIRECTION_DESC`)})`, id: '-city.name', alternate: 'city_id' },
    { name: `${t(`${TP}.m_COUNTRY`)} (${t(`${TP}.SORT_DIRECTION_ASC`)})`, id: 'country.name', alternate: 'country_id' },
    {
      name: `${t(`${TP}.m_COUNTRY`)} (${t(`${TP}.SORT_DIRECTION_DESC`)})`,
      id: '-country.name',
      alternate: 'country_id',
    },
  ];

  let SORT_DATES = [];
  if (viewType === PRODUCTION_VIEW_TYPES.PRODUCTION)
    SORT_DATES = [
      { name: `${t(`${TP}.SORT_YEAR`)}`, id: 'year', alternate: 'date' },
      { name: `${t(`${TP}.SORT_ENDYEAR_NAME`)}`, id: '-year', alternate: 'date' },
    ];

  const SORT_DATES_OTHERS = [
    { name: `${t(`${TP}.SORT_YEAR`)}`, id: 'date', alternate: 'date' },
    { name: `${t(`${TP}.SORT_ENDYEAR_NAME`)}`, id: '-date', alternate: 'date' },
  ];

  switch (page) {
    case 'production': {
      const queryKeys = Object.keys(query);
      const sortItems =
        queryKeys.filter(item => item === 'work_id').length > 0
          ? SORT_FIELDS.filter(item => item.alternate !== 'composer_id')
          : SORT_FIELDS;
      return [...sortItems, ...SORT_DATES]?.reduce((acc, item) => {
        queryKeys.filter(fields => fields === item.alternate).length === 0 && acc.push(item);
        return acc;
      }, []);
    }
    case 'performance': {
      const queryKeys = Object.keys(query);
      const sortItems =
        queryKeys.filter(item => item === 'work_id').length > 0
          ? SORT_FIELDS.filter(item => item.alternate !== 'composer_id')
          : SORT_FIELDS;
      return [...sortItems, ...SORT_DATES]?.reduce((acc, item) => {
        queryKeys.filter(fields => fields === item.alternate && item.alternate !== 'date').length === 0 &&
          acc.push(item);
        return acc;
      }, []);
    }
    case 'production stub': {
      return SORT_DATES_OTHERS;
    }
    case 'artist stub': {
      return [...SORT_FIELDS, ...SORT_DATES];
    }
    case 'company stub': {
      return [...SORT_FIELDS.filter(item => item.alternate !== 'company_id'), ...SORT_DATES, ...SORT_FIELDS_OPTIONAL];
    }
    case 'festival stub': {
      return [...SORT_FIELDS.filter(item => item.alternate !== 'company_id'), ...SORT_DATES, ...SORT_FIELDS_OPTIONAL];
    }
    case 'venues stub': {
      return [...SORT_FIELDS, ...SORT_DATES];
    }
    case 'works stub': {
      return [...SORT_FIELDS.filter(item => item.alternate !== 'work_id'), ...SORT_DATES, ...SORT_FIELDS_OPTIONAL];
    }
    case 'composer stub': {
      return [...SORT_FIELDS.filter(item => item.alternate !== 'composer_id'), ...SORT_DATES, ...SORT_FIELDS_OPTIONAL];
    }

    case 'videos': {
      return SORT_FIELDS_VIDEO;
    }
    default:
      return SORT_FIELDS;
  }
};

const fetchOptionsForDropdown = (options, query, callback, page, viewType, t) => {
  const { endpoint, ...restOfQuery } = query;

  const allPromises = options.map(option => {
    if (option.type === 'date') return { [option.type]: [{ id: '-1', name: option.label }] };
    if (option.type === 'sort') {
      const sortFields = fetchSortOptions(page, query, viewType, t);
      return { [option.type]: [...sortFields] };
    }

    return getOptions({ endpoint, query: { ...restOfQuery, aggregation_type: option.type } }).then(data => {
      if (data?.data?.length > 0)
        if (option.type === 'year_id' && page !== 'videos') {
          return { [option.type]: [...data.data] };
        } else return { [option.type]: [{ id: '-1', name: option.label }, ...data.data] };
    });
  });
  return Promise.all(allPromises).then(responses => {
    const values = {};
    // eslint-disable-next-line no-restricted-syntax,no-unused-vars
    for (const [key, value] of Object.entries(responses)) {
      // eslint-disable-next-line prefer-destructuring
      if (value) values[[Object.keys(value)]] = Object.values(value)[0];
    }
    callback(values);
    return values;
  });
};

const getSelectedValue = (state, type) => {
  if (type === 'sort') {
    const value = state.selectedValue[type]?.split(',');
    return value ? value[0] : state.selectedValue[type];
  }
  return state.selectedValue[type];
};

export default function Subfilter({
  options,
  query,
  onChange,
  isSmall,
  isNewSearch,
  initialData = {},
  seasonsOptions,
  tabViewType,
  selectedValueQuery = {},
  page,
  viewType,
  isStubPage,
  endpoint,
}) {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const [state, dispatch] = React.useReducer(filterReducer, {
    endpoint,
    ...query,
    selectedValue: {
      ...selectedValueQuery,
    },
  });
  const [toggleState, setToggleState] = React.useState(false);
  const [apiTriggered, updateTrigger] = React.useState(false);
  const [selectedFilter, setSelectedFilter] = React.useState(options[0]);
  const [dropdownOptions, setDropdownOptions] = React.useState(initialData);
  React.useEffect(() => {
    if (query && isNewSearch) {
      fetchOptionsForDropdown(
        options,
        { endpoint, ...query },
        responses => {
          if (responses) {
            setDropdownOptions(responses);
            setSelectedFilter(
              options?.filter(
                item => dropdownOptions[item.type]?.length > 1 || item.type === 'date' || item.type === 'sort',
              )?.[0],
            );
          }
        },
        page,
        viewType,
        t,
      );
    }
  }, [query]);

  const openDropdown = () => {
    if (query && options?.length > 0) {
      if (isNewSearch) {
        fetchOptionsForDropdown(
          options,
          { endpoint, ...query },
          responses => {
            if (responses) {
              setDropdownOptions(responses);
              updateTrigger(true);
              setSelectedFilter(
                options?.filter(
                  item => dropdownOptions[item.type]?.length > 1 || item.type === 'date' || item.type === 'sort',
                )?.[0],
              );
            }
          },
          page,
          viewType,
          t,
        );
        dispatch({
          type: 'RESET_QUERY',
          payload: {
            state: query,
            endpoint,
          },
        });
      } else
        dispatch({
          type: 'UPDATE_QUERY',
          payload: {
            state: query,
            endpoint,
          },
        });
    }
  };

  const toggleDrawer = (toggleOpen, value) => event => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    if (value) {
      setSelectedFilter(value);
    }
    if (!value) openDropdown();
    setToggleState(toggleOpen);
  };
  const showFilter = true;
  const { date_from, date_to } = selectedValueQuery;
  const defaultSort = !(date_from || date_to);

  const toggleList = () => (
    <>
      <div className={classes.header} role="presentation">
        <p className={classes.headerLabel}>{t(`${TP}.FILTERS`)}</p>

        <IconButton
          className={classes.mobileSearch_icon}
          color="inherit"
          aria-label="close"
          onClick={() => {
            setToggleState(false);
          }}
          size="large"
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </div>
      <div className={classes.fullList}>
        <div className={classes.list1} role="presentation">
          {options?.map((item, index) => {
            let showItem = dropdownOptions[item.type]?.length > 1 || item.type === 'date' || item.type === 'sort';

            if (tabViewType === 'upcoming' && (item.type === 'sort' || item.type === 'date')) showItem = false;
            if (showItem)
              return (
                <ListItem
                  selected={item.label === selectedFilter?.label}
                  className={item.label === selectedFilter?.label ? classes.selected : classes.content}
                  button
                  key={index}
                  onClick={toggleDrawer(true, item)}
                >
                  <ListItemText primary={item.label} />
                </ListItem>
              );
          })}
        </div>
        <div className={classes.list2} role="presentation">
          {selectedFilter && showFilter && (
            <FBToggleList
              query={{ aggregation_type: selectedFilter.type, ...state.query }}
              label={selectedFilter.label}
              state={{ ...state, selectedValue: selectedValueQuery }}
              dispatch={dispatch}
              type={selectedFilter.type}
              onSubmit={onChange}
              options={dropdownOptions[selectedFilter.type]?.filter(option => option.id != -1) || []}
              getSelectedValue={getSelectedValue}
              seasonsOptions={seasonsOptions}
              setToggleState={setToggleState}
              viewType={viewType}
              tabViewType={tabViewType}
              defaultSort={defaultSort}
            />
          )}
        </div>
      </div>
    </>
  );
  const toggleItem = (
    <>
      <Button onClick={toggleDrawer(true)} className={cx(classes.content, classes.customStyle, classes.toggleButton)}>
        <span className={classes.label}>{`${t(`${TP}.FILTERS`)}/${t(`${TP}.SORT`)}`}</span>
        <SortIcon className={classes.icon} />
      </Button>
      <SwipeableDrawer anchor="bottom" open={toggleState} onClose={toggleDrawer(false)} onOpen={toggleDrawer(true)}>
        {toggleList()}
      </SwipeableDrawer>
    </>
  );

  const subFilterList = () => (
    <>
      {options?.map((item, index) => (
        <FBDropdownButton
          key={`${item.type}-${index}`}
          query={{ ...state, aggregation_type: item.type }}
          label={item.label}
          state={{ ...state, selectedValue: selectedValueQuery }}
          dispatch={dispatch}
          type={item.type}
          onSubmit={onChange}
          tabViewType={tabViewType}
          options={dropdownOptions[item.type] || []}
          apiTriggered={apiTriggered}
          getSelectedValue={getSelectedValue}
          isSmall={isSmall}
          seasonsOptions={seasonsOptions}
          viewType={viewType}
          defaultSort={defaultSort}
          isStubPage={isStubPage}
          page={page}
          openDropdown={openDropdown}
        />
      ))}
    </>
  );

  return (
    <div className={classes.root}>
      {!isSmall && options.length > 0 && <span className={classes.sortLabel}>{t(`${TP}.FILTERS`)}</span>}
      {isSmall ? toggleItem : subFilterList()}
    </div>
  );
}
